import React, { useState, useCallback, useEffect } from "react";
import { config } from "@triare/auth-redux";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import { Button, Segmented, message, Spin, Dropdown, MenuProps } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Survey } from "../../../../types/survey";
import { SurveyBuilder } from "../../../Common/Survey/SurveyBuilder";
import {
  useSurveyCreateFull,
  useTempateDownload,
  useTemplateCopy,
  useTemplateCreate,
  useTemplateDelete,
  useTemplateGetById,
} from "../../../../hooks/api/surveys";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import {
  transformApiSurveyToApp,
  transformSurveyToApi,
} from "../../../../utils/transformSurveyToApi";
import { SurveyPreview } from "../../../Common/SurveyPreview";
import { useLocation, useNavigate, useParams } from "react-router";
import { cloneSurveyWithNewIds } from "../../../../utils/cloneSurveyForTemplate";
import Loading from "../../../Common/Loading";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import { downloadFromAnchor } from "../../../../utils";

const createEmptySurvey = (): Survey => ({
  status: "draft",
  title: "",
  items: [],
});

export default function TemplateViewContent(): React.ReactNode | null {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open, contextHolder } = useSimpleModal();
  const { setBreadcrumbs } = useBreadcrumbs();
  const templateGetById = useTemplateGetById(id);
  const surveyCreate = useSurveyCreateFull();
  const templateDelete = useTemplateDelete(id);
  const templateCopy = useTemplateCopy();
  const tempateDownload = useTempateDownload(id);
  const location = useLocation();
  const fromPage = location.state?.fromPage || "1";
  const [surveyData, setSurveyData] = useState<Survey>(createEmptySurvey());

  useEffect(() => {
    if (
      !templateGetById.loading &&
      !templateGetById.loading &&
      templateGetById.data
    ) {
      setSurveyData(transformApiSurveyToApp(templateGetById.data));
    }
  }, [templateGetById.data, templateGetById.loading, templateGetById.error]);

  useEffect(() => {
    if (
      !templateDelete.error &&
      !templateDelete.loading &&
      templateDelete.data
    ) {
      navigate(`/templates?current=${fromPage}`);
    }
  }, [
    templateDelete.error,
    templateDelete.loading,
    templateDelete.data,
    fromPage,
    navigate,
  ]);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Template", href: "/templates" },
      { title: "View" },
    ]);
  }, []);

  const handleSaveSurvey = useCallback(async () => {
    const surveyClone = cloneSurveyWithNewIds(surveyData);

    const apiData = transformSurveyToApi({
      ...surveyClone,
      status: "draft",
    });

    try {
      const res = await surveyCreate.fetch(apiData);

      if (res?.id) {
        message.success("Survey created");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to save survey");
    }
  }, [surveyData, surveyCreate]);

  const items: MenuProps["items"] = [
    {
      key: "download",
      type: "item",
      icon: <DownloadOutlined />,
      label: "Download",
      onClick: () => {
        tempateDownload.fetch().then((response) => {
          if (!response) return;
          downloadFromAnchor(
            response,
            `${templateGetById.data?.title}`,
            "application/pdf",
          );
        });
      },
    },
    {
      key: "delete",
      type: "item",
      icon: <DeleteOutlined style={{ color: "red" }} />,
      label: <span style={{ color: "red" }}>Delete</span>,
      onClick: () => {
        open({
          icon: <ExclamationCircleFilled />,
          title: "Delete template?",
          content: (
            <span>
              Are you sure you want to delete this template? This action cannot
              be undone and the template will be permanently removed.
            </span>
          ),
          cancelText: "Cancel",
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => templateDelete.fetch(),
        });
      },
    },
  ];

  useMessageSuccess([templateDelete], "Template deleted");
  useMessageSuccess([templateCopy], "Template copied");

  return (
    <SinglePageContainer
      title="View Template"
      extra={
        <>
          <Button
            onClick={() => {
              navigate(`/templates/${id}/edit`);
            }}
          >
            Edit
          </Button>
          <Button
            type="primary"
            onClick={handleSaveSurvey}
            loading={surveyCreate.loading}
          >
            Create survey
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={async () => {
              const res = await templateCopy.fetch(undefined, `${id}/copy`);
              if (res) {
                navigate(`/templates/${res.id}/edit`);
              }
            }}
          />
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </>
      }
      style={{ backgroundColor: "transparent" }}
    >
      <div>
        <SurveyPreview
          survey={surveyData}
          readOnly={false}
          onSubmit={(answers) => console.log("Answers:", answers)}
        />
      </div>
      {templateGetById.loading ||
        templateCopy.loading ||
        surveyCreate.loading ? (
        <Loading absolute />
      ) : null}
      {contextHolder}
    </SinglePageContainer>
  );
}
